import React from 'react';
import styled, { css } from 'styled-components';
import { space, SpaceProps } from 'styled-system';

import Drawer from '@material-ui/core/Drawer';
import { ModalProps } from '@material-ui/core';

import { ModalExitIcon } from 'shared/modals/v2-container';
import { heading3 } from 'components/core/typography';
import { BackButton } from './back-button';

export type BottomDrawerProps = {
  backButtonCopy?: string;
  handleBackButtonClick?: () => void;
  children: React.ReactNode;
  open: boolean;
  onClose?: () => void;
  heading?: string;
  subheading?: string;
  hideExitIcon?: boolean;
  fullHeight?: boolean;
  shouldCalculateIframeBottom?: boolean;
  shouldUseImportantZIndex?: boolean;
  shouldIncludePaperPadding?: boolean;
  iframeHeight?: number | null;
  viewportHeight?: number | null;
  parentOffset?: number | null;
  isIframe?: boolean;
  ModalProps?: Partial<ModalProps>;
};

export function BottomDrawer({
  backButtonCopy,
  handleBackButtonClick,
  children,
  onClose,
  heading,
  subheading,
  hideExitIcon,
  fullHeight,
  shouldCalculateIframeBottom = false,
  shouldUseImportantZIndex = true,
  shouldIncludePaperPadding = true,
  iframeHeight = null,
  viewportHeight = null,
  parentOffset = null,
  isIframe = false,
  ...props
}: BottomDrawerProps): JSX.Element {
  const bottomPosition =
    shouldCalculateIframeBottom && isIframe && iframeHeight && viewportHeight && parentOffset !== null
      ? Math.max(iframeHeight - viewportHeight - parentOffset, 0)
      : null;

  return (
    <StyledDrawer
      anchor='bottom'
      onClose={onClose}
      $bottomPosition={bottomPosition}
      $fullHeight={!!fullHeight}
      $shouldUseImportantZIndex={shouldUseImportantZIndex}
      $shouldIncludePaperPadding={shouldIncludePaperPadding}
      {...props}
    >
      {heading && <Heading>{heading}</Heading>}
      {subheading && <Subheading>{subheading}</Subheading>}
      {backButtonCopy && handleBackButtonClick && <BackButton copy={backButtonCopy} onClick={handleBackButtonClick} />}
      {!hideExitIcon && !!onClose && <StyledExitIcon onClick={onClose} />}
      {children}
    </StyledDrawer>
  );
}

type StyledDrawerProps = {
  isBackgroundVisible?: boolean;
  $bottomPosition?: number | null;
  $fullHeight?: boolean;
  $shouldUseImportantZIndex?: boolean;
  $shouldIncludePaperPadding?: boolean;
};

const StyledDrawer = styled(Drawer)<StyledDrawerProps>`
  /* Root: optional important z-index to override mui inline styles */
  ${({ $shouldUseImportantZIndex }) =>
    $shouldUseImportantZIndex &&
    css`
      &.MuiDrawer-root {
        z-index: 1400 !important;
      }
    `}

  .MuiBackdrop-root {
    background-color: ${({ theme, isBackgroundVisible }) => !isBackgroundVisible && theme.colors.greyOverlay};
  }

  > .MuiPaper-root.MuiDrawer-paper {
    z-index: 1500;
    /* Default matches MUI's white .MuiPaper-root; concierge overrides via --dcm-bottom-drawer-bg. */
    background-color: var(--dcm-bottom-drawer-bg, ${({ theme }) => theme.colors.white});
    /* MUI's Paper sets its own explicit text color (rgba(0, 0, 0, 0.87)) here,
       which shadows the inherited --dcm-color-text-primary for drawer content
       that doesn't set its own color (e.g. plain headings) - override it
       explicitly so those elements re-skin under Concierge Mode too. */
    --dcm-bottom-drawer-text-color: rgba(0, 0, 0, 0.87);
    .concierge-mode & {
      --dcm-bottom-drawer-text-color: var(--dcm-color-text-primary);
    }
    color: var(--dcm-bottom-drawer-text-color);
    ${({ $shouldIncludePaperPadding }) =>
      $shouldIncludePaperPadding &&
      css`
        padding: 25px 25px 50px;
      `}
    ${({ $fullHeight }) =>
      $fullHeight
        ? css`
            height: 100%;
          `
        : css`
            border-radius: 23px 23px 0px 0px;
          `}

    /* apply bottom padding only on iOS since Safari's bottom menu bar was interfering w buttons */
    @supports (-webkit-touch-callout: none) {
      padding-bottom: calc(49px + env(safe-area-inset-bottom));
    }

    /* Fix for positioning in iframe */
    ${({ $bottomPosition }) =>
      $bottomPosition !== null &&
      css`
        bottom: ${$bottomPosition}px !important;
      `}
  }
`;

const StyledExitIcon = styled(ModalExitIcon)`
  top: 25px;
  right: 23px;
  svg {
    fill: var(--dcm-bottom-drawer-exit-color, ${({ theme }) => theme.colors.grey[35]});
  }
`;

const Heading = styled.h3`
  ${heading3}
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const Subheading = styled.p`
  color: var(--dcm-color-text-secondary, ${({ theme }) => theme.colors.grey[45]});
  font-size: 12px;
  margin-top: 8px;
`;

type ElevatedSectionProps = SpaceProps & {
  borderPosition: 'bottom' | 'top';
};

export const ElevatedSection = styled.div<ElevatedSectionProps>`
  ${({ theme, borderPosition = `bottom` }) =>
    borderPosition === 'top'
      ? css`
          border-top: 1px solid ${theme.colors.grey[90]};
          box-shadow: 0px -3px 1px ${theme.colors.sectionShadow};
        `
      : css`
          border-bottom: 1px solid ${theme.colors.grey[90]};
          box-shadow: 0px 3px 1px ${theme.colors.sectionShadow};
        `}
  margin: 0 -25px;
  padding: 0 25px;
  ${space}
`;
